import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  TextField,
  Button,
  Typography,
  CircularProgress,
  Autocomplete,
  Chip,
} from '@mui/material';
import { nowIstIso } from './healthUtils';
import { isBillType } from './healthDownloadUtils';
import HealthDateTimeField from './HealthDateTimeField';
import HealthFreeSoloSelect from './HealthFreeSoloSelect';
import HealthAttachmentsSection from './HealthAttachmentsSection';
import HealthFilePreview from './HealthFilePreview';

const EMPTY_OPTIONS = { types: [], report_types: [], hospitals: [] };

export default function HealthNewFileDialog({
  open,
  onClose,
  onSaved,
  request,
  caseId,
}) {
  const [form, setForm] = useState({
    file_name: '',
    bill_number: '',
    report_type: '',
    type: '',
    hospital_name: '',
    date: nowIstIso(),
    tags: [],
  });
  const [fieldOptions, setFieldOptions] = useState(EMPTY_OPTIONS);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [tagOptions, setTagOptions] = useState([]);
  const [tagInput, setTagInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [localPreview, setLocalPreview] = useState(null);

  useEffect(() => {
    if (!open) setLocalPreview(null);
  }, [open]);

  useEffect(() => {
    if (!open || !caseId) return undefined;
    let cancelled = false;
    setForm({
      file_name: '',
      bill_number: '',
      report_type: '',
      type: '',
      hospital_name: '',
      date: nowIstIso(),
      tags: [],
    });
    setPendingFiles([]);
    setError('');
    setTagInput('');
    request(`/api/health/cases/${caseId}/field-options`)
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (!cancelled && data) setFieldOptions(data);
      })
      .catch(() => {});
    request('/api/health/tags')
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (!cancelled && data) setTagOptions(data.tags || []);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [open, caseId, request]);

  async function handleSave() {
    if (!form.type.trim()) {
      setError('Type is required');
      return;
    }
    if (!form.date) {
      setError('Date and time are required');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const formData = new FormData();
      if (form.file_name.trim()) formData.append('file_name', form.file_name.trim());
      if (form.bill_number.trim()) formData.append('bill_number', form.bill_number.trim());
      if (form.report_type.trim()) formData.append('report_type', form.report_type.trim());
      formData.append('type', form.type.trim());
      if (form.hospital_name.trim()) formData.append('hospital_name', form.hospital_name.trim());
      formData.append('date', form.date);
      const tagNames = [];
      for (const t of form.tags) {
        const name = typeof t === 'string' ? t : t.name;
        if (name && name.trim()) tagNames.push(name.trim());
      }
      formData.append('tags', JSON.stringify(tagNames));
      for (const f of pendingFiles) {
        formData.append('files', f);
      }

      const res = await request(`/api/health/cases/${caseId}/files`, {
        method: 'POST',
        body: formData,
        headers: {},
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setError(data.error || 'Failed to create file');
        return;
      }
      onSaved();
      onClose();
    } catch (e) {
      setError(e.message || 'Request failed');
    } finally {
      setSaving(false);
    }
  }

  const showBillNumber = isBillType(form.type);

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>New file</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              label="File name (optional)"
              fullWidth
              value={form.file_name}
              onChange={(e) => setForm((f) => ({ ...f, file_name: e.target.value }))}
            />
            <HealthFreeSoloSelect
              label="Report type"
              value={form.report_type}
              options={fieldOptions.report_types || []}
              onChange={(v) => setForm((f) => ({ ...f, report_type: v }))}
            />
            <HealthFreeSoloSelect
              label="Type"
              value={form.type}
              options={fieldOptions.types || []}
              onChange={(v) => setForm((f) => ({ ...f, type: v }))}
              required
            />
            {showBillNumber && (
              <TextField
                label="Bill number"
                fullWidth
                value={form.bill_number}
                onChange={(e) => setForm((f) => ({ ...f, bill_number: e.target.value }))}
              />
            )}
            <HealthFreeSoloSelect
              label="Hospital"
              value={form.hospital_name}
              options={fieldOptions.hospitals || []}
              onChange={(v) => setForm((f) => ({ ...f, hospital_name: v }))}
            />
            <HealthDateTimeField
              label="Document date"
              required
              value={form.date}
              onChange={(v) => setForm((f) => ({ ...f, date: v }))}
            />
            <Autocomplete
              multiple
              freeSolo
              options={tagOptions}
              getOptionLabel={(opt) => (typeof opt === 'string' ? opt : (opt && opt.name ? opt.name : ''))}
              value={form.tags}
              inputValue={tagInput}
              onInputChange={(_, v) => setTagInput(v)}
              onChange={(_, v) => setForm((f) => ({ ...f, tags: v }))}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => {
                  const label = typeof option === 'string' ? option : (option && option.name ? option.name : '');
                  if (!label) return null;
                  const tagProps = getTagProps({ index });
                  return <Chip {...tagProps} key={`${label}-${index}`} label={label} size="small" />;
                })
              }
              renderInput={(params) => <TextField {...params} label="Tags" placeholder="Add tags" />}
            />
            <HealthAttachmentsSection
              title="Attachments (optional, multiple pages/photos)"
              existingAttachments={[]}
              pendingFiles={pendingFiles}
              onPendingChange={setPendingFiles}
              onPreview={setLocalPreview}
              request={request}
              disabled={saving}
            />
            {error && (
              <Typography variant="body2" color="error">
                {error}
              </Typography>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? <CircularProgress size={24} /> : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <HealthFilePreview preview={localPreview} onClose={() => setLocalPreview(null)} request={request} />
    </>
  );
}
